import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Chip,
  Box,
  Paper,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import {
  School as SchoolIcon,
  EmojiEvents as TrophyIcon,
  AutoStories as BookIcon,
} from "@mui/icons-material";

const coursework = [
  "Introduction to Algorithms",
  "Data Structures (Java)",
  "Software Engineering (ASP.NET)",
  "Computer Architecture",
  "Networking & CyberSecurity",
  "Operating Systems (C)",
  "Artificial Intelligence (Python)",
  "Linear Algebra",
];

const scholarships = [
  { label: "Nexus DevDays Award Winner", year: "2026" },
  { label: "MMR Group Technology Scholarship", year: "2026" },
  { label: "Thomas F. Higginbotham Scholarship", year: "2025–2026" },
  { label: "UPE Scholarship (Honors) Award", year: "2024" },
  { label: "President's List & Priority Scholarship", year: "2022–2025" },
];

const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 2,
        flexShrink: 0,
        boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
      }}
    >
      <Icon sx={{ color: "white", fontSize: 21 }} />
    </Box>
    <Typography
      variant="h5"
      fontWeight="bold"
      sx={{ color: "var(--body_color)" }}
    >
      {title}
    </Typography>
  </Box>
);

export const EducationSection = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        mb: 4,
        borderRadius: 4,
        border: "1px solid var(--border-color)",
        background: "var(--card-bg)",
        boxShadow: "0 8px 32px rgba(102,126,234,0.08)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
      }}
    >
      <SectionHeader icon={SchoolIcon} title="Education" />
      <Divider sx={{ mb: 3 }} />

      {/* Degree Card */}
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.06) 100%)",
          border: "1px solid rgba(102,126,234,0.18)",
          mb: 3,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "4px",
            background:
              "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "4px 0 0 4px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="700"
            sx={{ color: "var(--body_color)" }}
          >
            Southeastern Louisiana University
          </Typography>
          <Chip
            label="May 2026"
            size="small"
            sx={{
              bgcolor: "rgba(102,126,234,0.12)",
              color: "#667eea",
              fontWeight: 700,
              border: "1px solid rgba(102,126,234,0.25)",
            }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mb: 0.4,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Bachelor of Science in Computer Science
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--body_color)", opacity: 0.65, mb: 2.5 }}
        >
          Hammond, LA &nbsp;•&nbsp; GPA: 4.0 / 4.0
        </Typography>

        {/* Coursework */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 0.8 }}>
          <BookIcon sx={{ fontSize: 16, color: "#667eea" }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "var(--body_color)",
              opacity: 0.6,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            Relevant Coursework
          </Typography>
        </Box>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.8 }}>
          {coursework.map((c, i) => (
            <Chip
              key={i}
              label={c}
              size="small"
              sx={{
                fontSize: "0.73rem",
                bgcolor: "rgba(102,126,234,0.09)",
                color: "var(--body_color)",
                border: "1px solid rgba(102,126,234,0.22)",
                fontWeight: 500,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "rgba(102,126,234,0.18)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 3px 8px rgba(102,126,234,0.15)",
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Scholarships & Awards */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 0.8 }}>
        <TrophyIcon sx={{ color: "#FFD700", fontSize: 22 }} />
        <Typography
          variant="subtitle1"
          fontWeight="700"
          sx={{ color: "var(--body_color)" }}
        >
          Scholarships & Academic Awards
        </Typography>
      </Box>
      <Grid container spacing={1.5}>
        {scholarships.map((award, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                borderRadius: 2,
                background: "var(--card-bg)",
                border: "1px solid var(--border-color)",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateX(5px)",
                  borderColor: "rgba(102,126,234,0.35)",
                  boxShadow: "0 3px 10px rgba(102,126,234,0.12)",
                  background:
                    "linear-gradient(90deg, rgba(102,126,234,0.05) 0%, transparent 100%)",
                },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{ color: "var(--body_color)", fontSize: "0.82rem", flex: 1 }}
              >
                {award.label}
              </Typography>
              <Chip
                label={award.year}
                size="small"
                sx={{
                  fontSize: "0.68rem",
                  bgcolor: "rgba(102,126,234,0.1)",
                  color: "#667eea",
                  fontWeight: 600,
                  ml: 1,
                  flexShrink: 0,
                  border: "1px solid rgba(102,126,234,0.2)",
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};
